import { jwtDecode } from 'jwt-decode';

import useAuth from '@frolight/hooks/useAuth';
import useRefreshToken from '@frolight/hooks/useRefreshToken';
import HTTPStatusError from '@frolight/utils/HTTPStatusError';

const useAuthenticatedAPIServiceCall = () => {

  const { auth } = useAuth();
  const refreshAccessToken = useRefreshToken();

  const isAboutToExpire = (accessToken) => {
    const THRESHOLD_IN_SECONDS = 120; // 2 minutes before aT expiry (which is 1 hour) => refresh
    const decodedToken = jwtDecode(accessToken.token);
    const expiration = decodedToken.exp || 0;
    const now = Math.floor(Date.now() / 1000);
    return now >= (expiration - THRESHOLD_IN_SECONDS);
  };

  const getNewAccessToken = async (externalAbortController) => {
    const abortController = externalAbortController || new AbortController();

    const refreshAccessTokenResponse = await refreshAccessToken(abortController);
    if (refreshAccessTokenResponse.error) {
      throw new Error('ERROR_REFRESHING_ACCESS_TOKEN');
    }
    return {
      token: refreshAccessTokenResponse.data.access_token,
      type: refreshAccessTokenResponse.data.token_type
    };
  };

  const executeRequest = async (fn, accessToken, params, isRetry = false) => {
    try {
      let response;
      if (params.opts) {
        response = await fn(params.abortController, accessToken, params.opts);
      } else {
        response = await fn(params.abortController, accessToken);
      }
      return response;
    } catch (err) {
      if (!isRetry) {
        if (err instanceof HTTPStatusError) {
          if (err.code === 401 && err.statusText === 'token_expired') {
            try {
              accessToken = await getNewAccessToken(params.abortController);
              return await executeRequest(fn, accessToken, params, true)
            } catch (refreshAccessTokenError) {
              throw refreshAccessTokenError;
            }
          }
        }
      }
      throw err;
    }
  };

  const authenticatedAPIServiceCall = async (serviceFn, params) => {
    if (!auth?.accessToken?.token) {
      throw new Error('NO_ACCESS_TOKEN');
    }

    let accessToken = auth.accessToken;
    if (isAboutToExpire(accessToken)) {
      // Refresh before calling
      try {
        accessToken = await getNewAccessToken(params?.abortController);
      } catch (err) {
        // Handle token refresh error
        throw new Error('ACCESS_TOKEN_REFRESH_ERROR');
      }
    }
    
    return await executeRequest(serviceFn, accessToken, params);
  }

  return authenticatedAPIServiceCall;
};

export default useAuthenticatedAPIServiceCall;
